/* 
Generated code: do not modify this file as it will be overwritten
Template: ImmutableJSRecord.cst

Known Issues/TODO:
- Using *Props type as Record constructor parameter is insufficient.
If a property needs to be a Record type, it should already be constructed.
- Rename Record fromJS factory method to fromJSON
- Create parseJSON method that translates JSON to Props



 */

/* eslint-disable import/order */

import { createSelector } from '@ngrx/store';
import { List, Map, Record, Seq, Set } from 'immutable';
import {
  CoordinatesRecord,
  CoordinatesProps,
} from 'src/store/generated/coordinates-record.generated';
import { AppState } from 'src/store/appstate';
import { getFromOrThrow, propValueOrDefault, tryOrDefault } from 'src/util/values';

export interface AddressProps {
  streetAddress: string | null;
  city: string | null;
  region: string | null;
  postalCode: string | null;
  country: string | null;
  county: string | null;
  gpsId: string | null;
  coordinates: CoordinatesProps | null;
}

export class AddressRecord extends Record<AddressProps>({
  streetAddress: null as any,
  city: null as any,
  region: null as any,
  postalCode: null as any,
  country: null as any,
  county: null as any,
  gpsId: null as any,
  coordinates: null as any,
}) {
  public static fromJS(json: {
    streetAddress: string | null;
    city: string | null;
    region: string | null;
    postalCode: string | null;
    country: string | null;
    county: string | null;
    gpsId: string | null;
    coordinates: any;
  }): AddressRecord {
    if (json == null) {
      throw new Error('AddressRecord.fromJS: parameter cannot be null');
    }
    return new AddressRecord({
      streetAddress: json.streetAddress,
      city: json.city,
      region: json.region,
      postalCode: json.postalCode,
      country: json.country,
      county: json.county,
      gpsId: json.gpsId,
      coordinates: json.coordinates != null ? new CoordinatesRecord(json.coordinates) : null,
    });
  }

  public static fromJSUnsafe(json: {
    streetAddress: string | null;
    city: string | null;
    region: string | null;
    postalCode: string | null;
    country: string | null;
    county: string | null;
    gpsId: string | null;
    coordinates: any;
  }): [AddressRecord, Error[]] {
    if (json == null) {
      throw new Error('AddressRecord.fromJSUnsafe: parameter cannot be null');
    }
    const errors: Error[] = [];

    const record = new AddressRecord({
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      streetAddress: tryOrDefault(
        () => json.streetAddress,
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      city: tryOrDefault(
        () => json.city,
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      region: tryOrDefault(
        () => json.region,
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      postalCode: tryOrDefault(
        () => json.postalCode,
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      country: tryOrDefault(
        () => json.country,
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      county: tryOrDefault(
        () => json.county,
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      gpsId: tryOrDefault(
        () => json.gpsId,
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      coordinates: tryOrDefault(
        () => (json.coordinates != null ? new CoordinatesRecord(json.coordinates) : null),
        (err) => errors.push(err),
      )!,
    });

    return [record, errors];
  }

  constructor(props: AddressProps) {
    super(props);
    if (props == null) {
      throw new Error('AddressRecord constructor parameter cannot be null');
    }
  }
}

export abstract class AddressViewModelBase implements AddressProps {
  protected readonly _props: AddressProps;
  private _json?: AddressProps;

  get streetAddress(): string | null {
    return this._props.streetAddress;
  }
  get city(): string | null {
    return this._props.city;
  }
  get region(): string | null {
    return this._props.region;
  }
  get postalCode(): string | null {
    return this._props.postalCode;
  }
  get country(): string | null {
    return this._props.country;
  }
  get county(): string | null {
    return this._props.county;
  }
  get gpsId(): string | null {
    return this._props.gpsId;
  }
  get coordinates(): CoordinatesProps | null {
    return this._props.coordinates;
  }

  constructor(props: AddressProps) {
    this._props = props;
  }

  public toJS() {
    return this.toJSON();
  }

  public toJSON() {
    if (this._json == null) {
      this._json = {
        streetAddress: this._props.streetAddress,
        city: this._props.city,
        region: this._props.region,
        postalCode: this._props.postalCode,
        country: this._props.country,
        county: this._props.county,
        gpsId: this._props.gpsId,
        coordinates: this._props.coordinates,
      };
    }
    return this._json;
  }
}
